import React, { useEffect, useRef } from 'react';
import {
  XMarkIcon,
  MinusIcon,
  ArrowsPointingOutIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useChatbot } from './ChatbotContext';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

export const ChatInterface: React.FC = () => {
  const { t } = useTranslation();
  const {
    messages,
    sendMessage,
    isMinimized,
    minimizeChatbot,
    maximizeChatbot,
    toggleChatbot,
    useGemini,
    toggleGemini,
  } = useChatbot();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (isMinimized) {
    return (
      <div className="bg-primary-500 fixed bottom-4 right-4 flex h-12 w-64 items-center justify-between rounded-t-lg px-4 py-2 text-white shadow-lg z-50">
        <span className="font-medium">
          {t('chatbot.title', 'AI Assistant')}
        </span>
        <div className="flex space-x-2">
          <button
            onClick={toggleGemini}
            className={`text-white hover:text-gray-200 focus:outline-none ${
              useGemini ? 'opacity-100' : 'opacity-50'
            }`}
            aria-label={
              useGemini
                ? t('chatbot.disableGemini', 'Disable Gemini AI')
                : t('chatbot.enableGemini', 'Enable Gemini AI')
            }
            title={
              useGemini
                ? t('chatbot.disableGemini', 'Disable Gemini AI')
                : t('chatbot.enableGemini', 'Enable Gemini AI')
            }
          >
            <SparklesIcon className="h-5 w-5" />
          </button>
          <button
            onClick={maximizeChatbot}
            className="text-white hover:text-gray-200 focus:outline-none"
            aria-label={t('chatbot.maximize', 'Maximize')}
          >
            <ArrowsPointingOutIcon className="h-5 w-5" />
          </button>
          <button
            onClick={toggleChatbot}
            className="text-white hover:text-gray-200 focus:outline-none"
            aria-label={t('chatbot.close', 'Close')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex h-[700px] w-6/12 flex-col rounded-lg bg-white shadow-xl">
      {/* Header */}
      <div className="bg-primary-500 flex items-center justify-between rounded-t-lg px-4 py-2 text-white">
        <span className="font-medium">
          {t('chatbot.title', 'AI Assistant')}
        </span>
        <div className="flex space-x-2">
          <button
            onClick={toggleGemini}
            className={`text-white hover:text-gray-200 focus:outline-none ${
              useGemini ? 'opacity-100' : 'opacity-50'
            }`}
            aria-label={
              useGemini
                ? t('chatbot.disableGemini', 'Disable Gemini AI')
                : t('chatbot.enableGemini', 'Enable Gemini AI')
            }
            title={
              useGemini
                ? t('chatbot.disableGemini', 'Disable Gemini AI')
                : t('chatbot.enableGemini', 'Enable Gemini AI')
            }
          >
            <SparklesIcon className="h-5 w-5" />
          </button>
          <button
            onClick={minimizeChatbot}
            className="text-white hover:text-gray-200 focus:outline-none"
            aria-label={t('chatbot.minimize', 'Minimize')}
          >
            <MinusIcon className="h-5 w-5" />
          </button>
          <button
            onClick={toggleChatbot}
            className="text-white hover:text-gray-200 focus:outline-none"
            aria-label={t('chatbot.close', 'Close')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput onSendMessage={sendMessage} />
    </div>
  );
};

export default ChatInterface;
